/**
 * Generates the CSS custom properties string for responsive typography.
 *
 * Produces two blocks:
 * 1. :root — Desktop/Common values (the default)
 * 2. @media (max-width: 767px) :root — Mobile overrides
 *
 * CSS variable naming convention (mirrors Figma variable names):
 *   --xui-font-size-{step}       → e.g. --xui-font-size-150: 16px
 *   --xui-lh-display-{step}      → e.g. --xui-lh-display-150: 16px
 *   --xui-lh-compact-{step}      → e.g. --xui-lh-compact-150: 20px
 *   --xui-lh-text-{step}         → e.g. --xui-lh-text-150: 22px
 *
 * Consumers can use these in CSS or styled-components:
 *   font-size: var(--xui-font-size-350);
 *   line-height: var(--xui-lh-compact-350);
 *
 * @see {@link ../tokens/responsive-typography.ts} for raw token data
 * @see {@link ../tokens/breakpoints.ts} for breakpoint definitions
 */

import {
  fontSize,
  lineHeightDisplay,
  lineHeightCompact,
  lineHeightText,
  SCALE_STEPS,
  DISPLAY_SCALE_STEPS,
} from "../tokens/responsive-typography";
import { MOBILE_MAX_WIDTH } from "../tokens/breakpoints";

function buildDeclarations(mode: "common" | "mobile"): string {
  const lines: string[] = [];

  for (const step of DISPLAY_SCALE_STEPS) {
    lines.push(`--xui-font-size-${step}:${fontSize[step][mode]}px`);
  }

  for (const step of DISPLAY_SCALE_STEPS) {
    lines.push(`--xui-lh-display-${step}:${lineHeightDisplay[step][mode]}px`);
  }

  for (const step of SCALE_STEPS) {
    lines.push(`--xui-lh-compact-${step}:${lineHeightCompact[step][mode]}px`);
  }

  for (const step of SCALE_STEPS) {
    lines.push(`--xui-lh-text-${step}:${lineHeightText[step][mode]}px`);
  }

  return lines.join(";");
}

let _cached: string | null = null;

/**
 * Returns the full CSS string for responsive typography custom properties.
 * The result is memoized — safe to call multiple times.
 */
export function generateTypographyCSS(): string {
  if (_cached) return _cached;

  const desktopVars = buildDeclarations("common");
  const mobileVars = buildDeclarations("mobile");

  _cached =
    `:root{${desktopVars}}` +
    `@media(max-width:${MOBILE_MAX_WIDTH}px){:root{${mobileVars}}}`;

  return _cached;
}

/**
 * Returns the CSS variable name for a given font-size scale step.
 * Useful when the Typography component needs to reference a CSS variable.
 *
 * @example cssVar.fontSize("350") → "var(--xui-font-size-350)"
 */
export const cssVar = {
  fontSize: (step: string) => `var(--xui-font-size-${step})`,
  lhDisplay: (step: string) => `var(--xui-lh-display-${step})`,
  lhCompact: (step: string) => `var(--xui-lh-compact-${step})`,
  lhText: (step: string) => `var(--xui-lh-text-${step})`,
} as const;
